import React, { memo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { InputTextarea } from 'primereact/inputtextarea'
import { Tooltip } from 'primereact/tooltip'

import { ChargePointImport } from '../../../../../common/redux/api/web-api'
import useText from '../../../../../common/hooks/useText'
import { Dialog } from '../../../../../common/uikit/Dialog'
import { Table } from '../../../../../common/uikit/Table'
import { useToast } from '../../../../../common/uikit/ToastProvider'
import { isAuraImport } from '../../../../../lib/isAura'
import { isDawnImport } from '../../../../../lib/isDawn'
import { isHaloImport } from '../../../../../lib/isHalo'
import { isLunaImport } from '../../../../../lib/isLuna'
import Text from '../../../../../ui/Text'
import { useValidateImportMutation, useImportChargepointsMutation } from '../../../../redux/api/chargePointsApi'
import { RootState } from '../../../../redux/store'

import { ImportConnectorSettings } from './import-connector-settings'

type ImportChargePointsDialogProps = {
    visible: boolean
    onSuccess: () => void
    onHide: () => void
}

type ImportSession = {
    input: string
    results: ChargePointImport[]
    isValid?: boolean
}

type ImportResultInfoProps = {
    ariaLabel: string
    icon: string
    messages: string[]
    severity: 'danger' | 'info' | 'warning'
    testId: string
}

const importResultSeverityClass: Record<ImportResultInfoProps['severity'], string> = {
    danger: 'text-red-500',
    info: 'text-blue-500',
    warning: 'text-yellow-600',
}

const ImportResultInfo = ({ ariaLabel, icon, messages, severity, testId }: ImportResultInfoProps) => {
    return (
        <span
            className={`import-result-info-tooltip inline-flex align-items-center justify-content-center text-xl ${importResultSeverityClass[severity]}`}
            role="img"
            tabIndex={0}
            aria-label={ariaLabel}
            data-testid={testId}
            data-pr-tooltip={messages.join(' • ')}
        >
            <i className={icon} aria-hidden="true" />
        </span>
    )
}

const initialImportSession: ImportSession = {
    input: '',
    results: [],
}

export const ImportChargePointsDialog = memo(function ImportChargePointsDialog({
    visible,
    onSuccess,
    onHide,
}: ImportChargePointsDialogProps): React.ReactElement {
    const text = useText()
    const toast = useToast()
    const [session, setSession] = useState<ImportSession>(initialImportSession)
    const [showInfoMessage, setShowInfoMessage] = useState(false)

    const companyId = useSelector((state: RootState) => state.user.selectedCompanyId)

    const [validateImport, { isLoading: isValidating }] = useValidateImportMutation()
    const [importChargePoints, { isLoading: isImporting }] = useImportChargepointsMutation()

    const doImportChargePoints = async () => {
        try {
            await importChargePoints({ companyId, importData: session.results }).unwrap()
            onSuccess()
        } catch {
            toast.notifyError(text('lblSomethingWentWrong'))
        }
    }

    const doValidateImport = async (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const clipboardText = event.clipboardData.getData('text')

        setSession({ input: clipboardText.trim(), results: [] })

        try {
            const results = await validateImport({ companyId, importData: clipboardText }).unwrap()

            setSession({
                input: '',
                results,
                isValid: !results.some((result) => result.hasError),
            })
        } catch (error: unknown) {
            const responseData = (error as { data?: unknown }).data

            setSession({
                input: '',
                results: Array.isArray(responseData) ? (responseData as ChargePointImport[]) : [],
                isValid: false,
            })
            toast.notifyError(text('msgImportChargersFailed'))
        }
    }

    const updateInput = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setSession({ input: event.target.value, results: [] })
    }

    const getDetails = (importRow: ChargePointImport) => {
        const details: string[] = []
        const connectors = importRow.connectors ?? []
        const firstConnector = connectors[0]

        if (importRow.description !== undefined && importRow.description !== null) {
            details.push(`${text('lblDescription')}: ${importRow.description}`)
        }
        if (importRow.partnerDescription !== undefined && importRow.partnerDescription !== null) {
            details.push(`${text('lblPartnerInfo')}: ${importRow.partnerDescription}`)
        }
        if (importRow.hasServiceAgreement !== undefined && importRow.hasServiceAgreement !== null) {
            details.push(`${text('lblHasServiceAgreement')}: ${importRow.hasServiceAgreement}`)
        }
        if (importRow.installationDate !== undefined && importRow.installationDate !== null) {
            details.push(`${text('lblInstallationDate')}: ${importRow.installationDate}`)
        }
        if (isAuraImport(importRow)) {
            connectors.forEach((connector, index) => {
                const connectorId = index + 1

                if (connector.mode !== undefined) {
                    details.push(`${text('lblSocketMode', { name: connectorId })}: ${connector.mode}`)
                }
                if (connector.nfcLocked !== undefined) {
                    details.push(`${text('lblSocketRfidLocked', { name: connectorId })}: ${connector.nfcLocked}`)
                }
            })
        } else if ((isDawnImport(importRow) || isLunaImport(importRow)) && firstConnector) {
            details.push(
                `${text('lblInstallationOfflinePhase')}: ${firstConnector.loadBalancerOfflinePhase}`,
                `${text('lblInstallationOfflineCurrent')}: ${firstConnector.loadBalancerOfflineCurrent}`
            )
        } else if (isHaloImport(importRow) && connectors.length > 1) {
            details.push(
                `${text('lblChargerMode')}: ${connectors[0].mode}`,
                `${text('lblSchukoMode')}: ${connectors[1].mode}`,
                `${text('lblRFIDLocked')}: ${connectors[0].nfcLocked}`
            )
        }

        return details.length > 0 ? details : [text('lblImportDetails')]
    }

    const renderDetails = (importRow: ChargePointImport) => (
        <ImportResultInfo
            ariaLabel={text('lblImportDetails')}
            icon="pi pi-info-circle"
            messages={getDetails(importRow)}
            severity="info"
            testId={`import-details-${importRow.id}`}
        />
    )

    const renderValidationMessages = (importRow: ChargePointImport) => {
        const errorReasons = importRow.errorReason ?? []
        const warningReasons = importRow.warningReason ?? []
        const reasons = errorReasons.concat(importRow.hasError ? [] : warningReasons)

        return reasons.length > 0 ? (
            <ImportResultInfo
                ariaLabel={text('lblShowImportValidationMessages')}
                icon="pi pi-exclamation-triangle"
                messages={reasons}
                severity={importRow.hasError ? 'danger' : 'warning'}
                testId={`import-validation-messages-${importRow.id}`}
            />
        ) : null
    }

    const header = (
        <div className="flex align-items-center gap-1">
            <span>{text('lblChargersImportModal')}</span>
            <Button
                type="button"
                icon="pi pi-info-circle"
                severity="info"
                text
                rounded
                aria-label={text('lblImportDetails')}
                tooltip={text('lblImportDetails')}
                tooltipOptions={{ position: 'top' }}
                onClick={() => setShowInfoMessage((showInfo) => !showInfo)}
            />
        </div>
    )

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label={text('btnCancel')} type="button" text onClick={onHide} />
            <Button
                label={text('btnImport')}
                type="button"
                disabled={!session.isValid || isValidating || isImporting}
                loading={isImporting}
                onClick={doImportChargePoints}
                data-testid="import-chargepoints-button"
            />
        </div>
    )

    return (
        <Dialog
            header={header}
            visible={visible}
            onHide={onHide}
            focusOnShow={false}
            style={{ width: '60rem' }}
            breakpoints={{ '960px': '90vw', '641px': '95vw' }}
            footer={footer}
            data-testid="import-charge-points-dialog"
        >
            {showInfoMessage ? (
                <section
                    className="max-h-20rem overflow-y-auto p-3 border-1 border-200 border-round mb-3"
                    data-testid="info-msg"
                >
                    <Text code="msgImportChargersFileTip" />
                </section>
            ) : null}

            <div className="mb-3">
                <InputTextarea
                    id="import-charge-points-input"
                    className="w-full"
                    placeholder={text('msgPasteLinesField')}
                    aria-label={text('msgPasteLinesField')}
                    value={session.input}
                    onChange={updateInput}
                    onPaste={doValidateImport}
                    autoFocus
                    rows={6}
                    autoResize
                    disabled={isValidating || isImporting}
                    data-testid="paste-textarea"
                />
            </div>

            {session.results.length > 0 ? (
                <>
                    <Tooltip target=".import-result-info-tooltip" position="top" />
                    <Table
                        value={session.results}
                        dataKey="id"
                        scrollable
                        scrollHeight="24rem"
                        rowClassName={(importRow: ChargePointImport) => ({
                            'bg-red-50': !!importRow.hasError,
                            'bg-yellow-50': !importRow.hasError && (importRow.warningReason?.length ?? 0) > 0,
                        })}
                    >
                        <Column field="id" header={text('lblSerialNumberColumn')} />
                        <Column field="name" header={text('lblName')} />
                        <Column
                            field="supplyCurrent"
                            header={text('lblSupplyCurrent')}
                            body={(importRow: ChargePointImport) =>
                                importRow.supplyCurrent ? `${importRow.supplyCurrent} A` : null
                            }
                        />
                        <Column
                            header={text('lblSocketSettings')}
                            body={(importRow: ChargePointImport) => <ImportConnectorSettings chargePoint={importRow} />}
                        />
                        <Column header={text('lblDetails')} body={renderDetails} />
                        <Column header={text('lblErrorReason')} body={renderValidationMessages} />
                    </Table>
                </>
            ) : null}
        </Dialog>
    )
})
